import Logo from "./logo";
import WinampVisualizer from "./winamp-visualizer";

// Classic Mac's menu bar had an apple at the far left, so the Classic Mac
// skin keeps the wordmark and sets one beside it — with the era's lowercase
// "i" in front, which is the joke the whole skin is making. Drawn here rather
// than traced from anyone's artwork: a plain six-stripe apple silhouette in
// the period palette.
function AppleMark() {
  return (
    <svg
      className="skin-logo__apple"
      width="24"
      height="28"
      viewBox="0 0 26 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="presentation"
    >
      <defs>
        <linearGradient id="skin-logo-apple" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#61bb46" />
          <stop offset="17%" stopColor="#61bb46" />
          <stop offset="17%" stopColor="#fdb827" />
          <stop offset="34%" stopColor="#fdb827" />
          <stop offset="34%" stopColor="#f5821f" />
          <stop offset="50%" stopColor="#f5821f" />
          <stop offset="50%" stopColor="#e03a3e" />
          <stop offset="67%" stopColor="#e03a3e" />
          <stop offset="67%" stopColor="#963d97" />
          <stop offset="84%" stopColor="#963d97" />
          <stop offset="84%" stopColor="#009ddc" />
          <stop offset="100%" stopColor="#009ddc" />
        </linearGradient>
      </defs>
      <path
        fill="url(#skin-logo-apple)"
        d="M17.6 8.7c-1.3 0-2.4.5-3.2 1-.5.3-1 .3-1.5 0-.9-.5-2-1-3.3-1C6.2 8.7 3 12 3 17c0 5.4 3.6 10.6 6.3 10.6 1 0 1.7-.3 2.4-.6.5-.2 1-.4 1.5-.4s1 .2 1.5.4c.7.3 1.4.6 2.4.6 2.7 0 6.3-5.2 6.3-10.6 0-5-3.2-8.3-6.8-8.3Z"
      />
      <path
        fill="#61bb46"
        d="M13.9 7.6c1.5-.2 3-1.1 3.9-2.3.8-1.1 1.3-2.6 1.1-4-1.4.1-3 1-4 2.1-.8 1-1.4 2.5-1 4.2Z"
      />
    </svg>
  );
}

// The Alien skin's saucer runs a long, mostly-idle cycle: it drops in, beams
// the wordmark up, holds an empty sky, then puts it back. All CSS — a popup
// that is usually open for seconds should not pay for an animation loop.
function AlienAbduction({ isPro, trialMode }) {
  return (
    <div className="skin-logo__alien">
      <div className="skin-logo__ufo" aria-hidden="true">
        <span className="skin-logo__ufo-dome" />
        <span className="skin-logo__ufo-hull" />
      </div>
      <div className="skin-logo__beam" aria-hidden="true" />
      <div className="skin-logo__abductee">
        <Logo isPro={isPro} trialMode={trialMode} />
      </div>
    </div>
  );
}

export default function SkinLogo({
  skin,
  isPro,
  trialMode,
  trackName,
  slowControl,
  playing,
  announcement,
}) {
  const mark = renderMark({
    skin,
    isPro,
    trialMode,
    trackName,
    slowControl,
    playing,
  });

  if (!announcement) return mark;

  // Picking a mood closes the sheet, so the header is where the confirmation
  // lands. The mark stays mounted underneath and the two cross-fade on one
  // timeline: swapping the elements outright made the name appear from
  // nowhere and the mark snap back at the end.
  return (
    <div className="skin-logo__swap" key={announcement}>
      <div className="skin-logo__swap-mark">{mark}</div>
      <div className="skin-logo__swap-name">
        <span>{announcement}</span>
      </div>
    </div>
  );
}

function renderMark({ skin, isPro, trialMode, trackName, slowControl, playing }) {
  if (skin === "winamp") {
    return (
      <WinampVisualizer
        trackName={trackName}
        slowControl={slowControl}
        playing={playing}
      />
    );
  }

  // The wordmark moves to the window's title bar as "iSlowed", so the header
  // carries the apple on its own.
  if (skin === "macos") {
    return (
      <div className="skin-logo__mac">
        <AppleMark />
      </div>
    );
  }

  if (skin === "alien") {
    return <AlienAbduction isPro={isPro} trialMode={trialMode} />;
  }

  // Dracula: drops gather under the wordmark and run off it. Three of them on
  // offset cycles, so it drips irregularly instead of in time with itself.
  if (skin === "dracula") {
    return (
      <div className="skin-logo__dracula">
        <Logo isPro={isPro} trialMode={trialMode} />
        <span className="skin-logo__drip skin-logo__drip--1" aria-hidden="true" />
        <span className="skin-logo__drip skin-logo__drip--2" aria-hidden="true" />
        <span className="skin-logo__drip skin-logo__drip--3" aria-hidden="true" />
      </div>
    );
  }

  return <Logo isPro={isPro} trialMode={trialMode} />;
}
